import { Component, Input, OnInit } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { HttpClient } from '@angular/common/http';
import { IonicModule, ModalController } from '@ionic/angular';
import { NgSelectModule } from '@ng-select/ng-select';
import { firstValueFrom } from 'rxjs';

/**
 *  TANTERMEN KÍVÜLI, DIGITÁLIS MUNKAREND ELRENDELÉSE (backend: digitalis-munkarend.js).
 *
 *  A tanév rendjéről szóló rendelet (2026/2027: 1/2026. (VII. 31.) OGYM r. 5. §) szerint a gimnázium és
 *  a szakgimnázium 9–11. évfolyamán 2027. május 3–7. között az igazgató döntése szerint digitális
 *  munkarend szervezhető. A naptárba beemelt bejegyzésből indítva az időszak és a 9–11. évfolyam elő
 *  van töltve; az osztályok egyenként is választhatók. Kézzel (admin felület) bármely időszakra.
 *
 *  Hatás: a napló képernyőn és a napló nyomtatványán jelölés, a szülő/tanuló kezdőlapján kártya.
 *  A mulasztás számítása nem változik (a jogszabály nem ad eltérő szabályt) – a jelenlét itt az
 *  online részvétel.
 */
@Component({
  standalone: true,
  imports: [IonicModule, CommonModule, FormsModule, NgSelectModule],
  selector: 'app-digitalis-munkarend-modal',
  templateUrl: './digitalis-munkarend-modal.component.html',
  styleUrls: ['./digitalis-munkarend-modal.component.scss'],
})
export class DigitalisMunkarendModalComponent implements OnInit {
  @Input() intezmeny = '';
  /** Az indító naptárbejegyzés (ha onnan nyílik): azonosító, időszak, leírás. */
  @Input() tktId: string | null = null;
  @Input() tol = '';
  @Input() ig = '';
  @Input() tema = '';
  /** Előtöltött évfolyamok (a rendelet szerint 9–11). */
  @Input() evfolyamok: number[] = [9, 10, 11];

  osztalyok: any[] = [];
  evfolyamLista: number[] = [];
  valasztottEvf: Record<number, boolean> = {};
  valasztottOsztalyok: string[] = [];
  ok = '';
  jogalap = '';
  meglevok: any[] = [];
  szerkesztheto = false;
  toltodik = false;
  fut = false;
  hiba = '';
  kesz = false;

  constructor(private http: HttpClient, private modalCtrl: ModalController) {}

  private get intPath(): string { return encodeURI(String(this.intezmeny || '').replace(/^\/+|\/+$/g, '')); }

  /** A dátum tanéve (kezdő év): augusztustól az új tanév. */
  private tanevE(d: string): number {
    const [y, m] = String(d || '').split('-').map(Number);
    if (!y) { const n = new Date(); return n.getMonth() >= 7 ? n.getFullYear() : n.getFullYear() - 1; }
    return m >= 8 ? y : y - 1;
  }

  async ngOnInit() {
    this.tol = String(this.tol || '').slice(0, 10);
    this.ig = String(this.ig || this.tol || '').slice(0, 10);
    //  A rendeletből beemelt bejegyzés leírása „Forrás: <rendelet címe>” – ebből a jogalap.
    const forras = (String(this.tema || '').match(/Forrás:\s*(.+?)\s*$/) || [])[1];
    this.jogalap = forras ? `${forras} 5. §` : (this.tktId ? 'A tanév rendjéről szóló rendelet' : '');
    this.ok = this.tktId ? 'Az igazgató döntése a tanév rendje szerinti digitális munkarendről.' : '';
    for (const e of this.evfolyamok || []) this.valasztottEvf[e] = true;
    await this.betolt();
  }

  private betoltottTanev: number | null = null;

  /** Más tanévre átírt kezdőnapnál az osztálylista is az új tanévé. */
  tolValtozott() {
    if (this.tol && this.tanevE(this.tol) !== this.betoltottTanev) this.betolt();
  }

  async betolt() {
    this.toltodik = true; this.hiba = '';
    try {
      const tanev = this.tanevE(this.tol);
      this.betoltottTanev = tanev;
      const [oszt, lista]: any[] = await Promise.all([
        firstValueFrom(this.http.get<any[]>(`/api/nyilvantartas/osztaly/${tanev}/${this.intPath}`)).catch(() => []),
        firstValueFrom(this.http.get<any>(`/api/digitalis-munkarend/lista/${this.intPath}?`
          + new URLSearchParams(this.tktId ? { tkt: this.tktId } : { tanev: String(tanev) }).toString())),
      ]);
      this.osztalyok = (Array.isArray(oszt) ? oszt : [])
        .filter((o: any) => Number(o?.evfolyam) > 0)
        .map((o: any) => ({ id: String(o.id), nev: o.osztaly, evfolyam: Number(o.evfolyam) }))
        .sort((a: any, b: any) => a.evfolyam - b.evfolyam || String(a.nev).localeCompare(String(b.nev), 'hu'));
      this.evfolyamLista = [...new Set(this.osztalyok.map((o: any) => o.evfolyam))].sort((a, b) => a - b);
      this.meglevok = lista?.idoszakok || [];
      this.szerkesztheto = !!lista?.szerkesztheto;
    } catch (e: any) { this.hiba = e?.error?.error || 'A digitális munkarend adatai nem tölthetők be.'; }
    this.toltodik = false;
  }

  get kijeloltEvfolyamok(): number[] {
    return Object.entries(this.valasztottEvf).filter(([, v]) => v).map(([k]) => Number(k)).sort((a, b) => a - b);
  }

  /** Az előnézet: mely osztályok kerülnek digitális munkarendbe. */
  get erintettOsztalyok(): any[] {
    const evf = new Set(this.kijeloltEvfolyamok);
    const kij = new Set(this.valasztottOsztalyok);
    return this.osztalyok.filter((o: any) => evf.has(o.evfolyam) || kij.has(o.id));
  }

  /** Az évfolyam-szűrőn kívül egyenként választható osztályok. */
  get kulonOsztalyok(): any[] {
    const evf = new Set(this.kijeloltEvfolyamok);
    return this.osztalyok.filter((o: any) => !evf.has(o.evfolyam));
  }

  get menthetoE(): boolean {
    return !!this.szerkesztheto && !!this.tol && !!this.ig && this.ig >= this.tol && this.erintettOsztalyok.length > 0 && !this.fut;
  }

  osztalyNevek(x: any): string {
    return (x?.osztalyok || []).map((o: any) => o.nev).join(', ') || '—';
  }

  async ment() {
    if (!this.menthetoE) return;
    this.fut = true; this.hiba = '';
    try {
      const evf = new Set(this.kijeloltEvfolyamok);
      await firstValueFrom(this.http.post(`/api/digitalis-munkarend/${this.intPath}`, {
        tol: this.tol, ig: this.ig, evfolyamok: this.kijeloltEvfolyamok,
        //  Az évfolyamként már kijelölt osztályt nem soroljuk fel külön is.
        osztalyok: this.valasztottOsztalyok.filter((id) => !evf.has(this.osztalyok.find((o: any) => o.id === id)?.evfolyam)),
        ok: this.ok, jogalap: this.jogalap, tkt_id: this.tktId,
      }));
      this.kesz = true;
      this.valasztottOsztalyok = [];
      await this.betolt();
    } catch (e: any) { this.hiba = e?.error?.error || 'Az elrendelés nem sikerült.'; }
    this.fut = false;
  }

  async torol(x: any) {
    if (!x?.id || !confirm(`Visszavonod a(z) ${x.tol} – ${x.ig} digitális munkarendet (${this.osztalyNevek(x)})?`)) return;
    try {
      await firstValueFrom(this.http.request('delete', `/api/digitalis-munkarend/tetel/${x.id}`,
        { params: { intezmeny: String(this.intezmeny || '').replace(/^\/+|\/+$/g, '') } }));
      this.kesz = true;
      await this.betolt();
    } catch (e: any) { this.hiba = e?.error?.error || 'A visszavonás nem sikerült.'; }
  }

  bezar() { this.modalCtrl.dismiss({ valtozott: this.kesz }); }
}
